import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBanners } from '../../redux/menu/BannersSlice';
import { FaMapMarkerAlt } from 'react-icons/fa';

const AutoSwipeBanner = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  const { banners, status } = useSelector((state) => state.banner);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Extract banners from the new nested structure
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);

  // Fetch banners when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchBanners(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Auto-swipe functionality
  useEffect(() => {
    if (!bannerList || bannerList.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === bannerList.length - 1 ? 0 : prevIndex + 1
      );
    }, 3500); // Change image every 3.5 seconds for more dynamic feel

    return () => clearInterval(interval);
  }, [bannerList]);



  if (status === 'loading') {
    return (
      <div className="w-full h-24 sm:h-28 md:h-32 lg:h-40 xl:h-56 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse rounded-xl sm:rounded-2xl mx-auto max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-xl shadow-xl sm:shadow-2xl border-2 sm:border-3 md:border-4" style={{ borderColor: '#e6d4b8' }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-600 text-xs sm:text-sm font-medium">Loading banners...</div>
        </div>
      </div>
    );
  }

  if (status === 'failed' || !bannerList || bannerList.length === 0) {
    return (
      <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-xl">
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border-2 sm:border-3 md:border-4 backdrop-blur-sm bg-gradient-to-br from-orange-100 to-yellow-100" style={{ borderColor: '#e6d4b8' }}>
          <div className="relative h-24 sm:h-28 md:h-32 lg:h-40 xl:h-56 flex items-center justify-center">
            <div className="text-center px-3 sm:px-4 md:px-6">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-2 sm:mb-3">🍽️</div>
              <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 poppins">
                Let's grab some tasty foods
              </div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 sm:mt-2">
                Browse our delicious menu below
              </div>
              {profile?.name && (
                <div className="text-xs sm:text-sm md:text-base font-semibold text-primary mt-2 sm:mt-3 poppins">
                  {profile.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-xl">
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border-2 sm:border-3 md:border-4 backdrop-blur-sm bg-gradient-to-br from-white/10 to-white/5" style={{ borderColor: '#e6d4b8' }}>
        {/* Banner Images */}
        <div className="relative h-24 sm:h-28 md:h-32 lg:h-40 xl:h-56">
          {bannerList.map((banner, index) => (
            <div
              key={banner.id || index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
                index === currentIndex 
                  ? 'opacity-100 scale-100 translate-x-0' 
                  : 'opacity-0 scale-105 translate-x-4'
              }`}
            >
              <img
                src={banner.DocImage?.imageUrl}
                alt={`Banner ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Gradient overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              
              {/* Store name overlay - top left with location icon */}
              <div className="absolute top-1 sm:top-1.5 md:top-2 left-1 sm:left-1.5 md:left-2 bg-gray-800 rounded px-1 sm:px-1.5 md:px-2 py-0.5 sm:py-1 shadow-lg border border-gray-600 opacity-50">
                <div className="flex items-center space-x-0.5 sm:space-x-1">
                  <FaMapMarkerAlt className="text-white text-xs drop-shadow-md" />
                  <span className="text-white text-xs font-semibold poppins drop-shadow-md">
                    {profile?.name || 'Store Name'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary/10 via-transparent to-primary/10 opacity-50"></div>
      </div>
    </div>
  );
};

export default AutoSwipeBanner;
